import { ReactNode } from 'react';

import { Box, Button, SxProps, Theme, Typography } from '@mui/material';

type InlineCtaAction = Readonly<{
  label: string;
  onClick: () => void;
  id: string;
  disabled?: boolean;
}>;

type InlineCtaCardProps = Readonly<{
  title: string;
  supportingText: string;
  primaryAction: InlineCtaAction;
  secondaryAction?: InlineCtaAction;
  icon?: ReactNode;
  stacked?: boolean;
}>;

export const InlineCtaCard = ({
  title,
  supportingText,
  primaryAction,
  secondaryAction,
  icon,
  stacked = false,
}: InlineCtaCardProps) => (
  <Box sx={cardSx}>
    {icon}
    <Box sx={textSx}>
      <Typography variant="text-lg-semibold">{title}</Typography>
      <Typography variant="text-sm-regular" sx={supportingTextSx}>
        {supportingText}
      </Typography>
    </Box>
    <Box sx={[actionsSx, stacked && stackedActionsSx] as SxProps<Theme>}>
      {secondaryAction && (
        <Button
          id={secondaryAction.id}
          variant="contained"
          size="md"
          hierarchy="Secondary gray"
          disabled={secondaryAction.disabled}
          onClick={secondaryAction.onClick}
        >
          {secondaryAction.label}
        </Button>
      )}
      <Button
        id={primaryAction.id}
        variant="contained"
        size="md"
        hierarchy="Primary"
        disabled={primaryAction.disabled}
        onClick={primaryAction.onClick}
      >
        {primaryAction.label}
      </Button>
    </Box>
  </Box>
);

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  padding: backrs.spacing['spacing-3xl'],
  width: '100%',
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
});

const textSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
});

const actionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
});

const stackedActionsSx: SxProps<Theme> = ({ backrs }) => ({
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const supportingTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
